/**
 * Atralend Ad Attribution & Conversion Tracking
 * Supports: Google Ads, Meta Pixel, LinkedIn Insight Tag
 * 
 * Pixel IDs are configured here as publishable keys.
 * Server-side conversions are handled via the sync-ad-conversions edge function.
 */

// Publishable pixel/tag IDs — these are safe to embed in client code
// The user will update these with their actual IDs
const TRACKING_CONFIG = {
  metaPixelId: '', // e.g. '123456789012345'
  googleAdsId: '', // e.g. 'AW-123456789'
  linkedInPartnerId: '', // e.g. '1234567'
  googleAdsConversionLabel: '', // e.g. 'abc123'
} as const;

// Type declarations for tracking globals
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    lintrk?: (action: string, data: Record<string, unknown>) => void;
    _linkedin_data_partner_ids?: string[];
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Initialize Meta Pixel
 */
const initMetaPixel = () => {
  if (!TRACKING_CONFIG.metaPixelId || window.fbq) return;
  
  const n = (window.fbq = function (...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (n as any).callMethod
      ? (n as any).callMethod(...args)
      : (n as any).queue.push(args);
  });
  if (!window._fbq) window._fbq = n;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).push = n;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).loaded = true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).version = '2.0';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).queue = [];

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);

  window.fbq('init', TRACKING_CONFIG.metaPixelId);
  window.fbq('track', 'PageView');
};

/**
 * Initialize LinkedIn Insight Tag
 */
const initLinkedIn = () => {
  if (!TRACKING_CONFIG.linkedInPartnerId || window.lintrk) return;
  
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(TRACKING_CONFIG.linkedInPartnerId);

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  document.head.appendChild(s);
};

/**
 * Initialize Google Ads conversion tracking (extends existing gtag)
 */
const initGoogleAds = () => {
  if (!TRACKING_CONFIG.googleAdsId || !window.gtag) return;
  window.gtag('config', TRACKING_CONFIG.googleAdsId);
};

/**
 * Initialize all tracking pixels
 * Call once on app load
 */
export const initializeTracking = () => {
  initMetaPixel();
  initGoogleAds();
  initLinkedIn();
};

/**
 * Standard conversion events mapped across platforms
 */
export type ConversionEvent =
  | 'quiz_started'
  | 'quiz_completed'
  | 'calculator_used'
  | 'loan_application_started'
  | 'loan_application_submitted'
  | 'phone_submitted'
  | 'chatbot_qualified'
  | 'pre_approval_started'
  | 'referral_shared';

// Map our events to platform-specific event names
const META_EVENT_MAP: Record<ConversionEvent, string> = {
  quiz_started: 'InitiateCheckout',
  quiz_completed: 'Lead',
  calculator_used: 'ViewContent',
  loan_application_started: 'AddToCart',
  loan_application_submitted: 'Purchase',
  phone_submitted: 'Contact',
  chatbot_qualified: 'CompleteRegistration',
  pre_approval_started: 'StartTrial',
  referral_shared: 'CustomizeProduct',
};

/**
 * Track a conversion event across all platforms
 */
export const trackConversion = (
  event: ConversionEvent,
  value?: number,
  metadata?: Record<string, unknown>
) => {
  const currency = 'CAD';

  // Google Analytics (GA4) - always available
  if (window.gtag) {
    window.gtag('event', event, {
      value,
      currency,
      ...metadata,
    });
  }

  // Google Ads conversion
  if (window.gtag && TRACKING_CONFIG.googleAdsId && TRACKING_CONFIG.googleAdsConversionLabel) {
    window.gtag('event', 'conversion', {
      send_to: `${TRACKING_CONFIG.googleAdsId}/${TRACKING_CONFIG.googleAdsConversionLabel}`,
      value,
      currency,
    });
  }

  // Meta Pixel
  if (window.fbq && TRACKING_CONFIG.metaPixelId) {
    const metaEvent = META_EVENT_MAP[event] || event;
    window.fbq('track', metaEvent, {
      value,
      currency,
      ...metadata,
    });
  }

  // LinkedIn
  if (window.lintrk && TRACKING_CONFIG.linkedInPartnerId) {
    window.lintrk('track', { conversion_id: event });
  }

  // Also send to GA4 custom event for unified reporting
  if (window.gtag) {
    window.gtag('event', `atralend_${event}`, {
      event_category: 'conversion',
      event_label: event,
      value,
      ...metadata,
    });
  }
};

/**
 * Track page views across platforms (call on route change)
 */
export const trackPageView = (path: string) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', { page_path: path });
  }
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

/**
 * Extract UTM parameters from URL
 */
export const getUTMParams = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];
  const result: Record<string, string> = {};
  
  utmKeys.forEach(key => {
    const val = params.get(key);
    if (val) result[key] = val;
  });
  
  // Persist UTM params in sessionStorage for later attribution
  if (Object.keys(result).length > 0) {
    sessionStorage.setItem('atralend_utm', JSON.stringify(result));
  }
  
  return result;
};

/**
 * Get stored UTM params (from any page during session)
 */
export const getStoredUTMParams = (): Record<string, string> => {
  try {
    return JSON.parse(sessionStorage.getItem('atralend_utm') || '{}');
  } catch {
    return {};
  }
};
